import React, { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import env from '../../../../environments/environment'
import { DISCLAIMER_SHORT } from '../../../../constants/compliance'

import './ContactUsQuoteForm.component.scss'

const RECAPTCHA_V3_SITE_KEY = env.integrations.google_recaptcha.key
const API_BASE = env.api.base_url

const INITIAL_STATE = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  product: '',
  zipCode: '',
}

const ContactUsQuoteForm = () => {
  const [formData, setFormData] = useState(INITIAL_STATE)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (document.querySelector(`script[src*="recaptcha/api.js"]`)) return

    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_V3_SITE_KEY}`
    script.async = true
    document.head.appendChild(script)
  }, [])

  const getRecaptchaToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) {
        reject(new Error('reCAPTCHA not loaded'))
        return
      }
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(RECAPTCHA_V3_SITE_KEY, { action: 'contact_submission' }).then(resolve).catch(reject)
      })
    })
  }, [])

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (submitting) return

    setSubmitting(true)

    try {
      const recaptchaToken = await getRecaptchaToken()

      const response = await fetch(`${API_BASE}quoteSubmission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, recaptchaToken }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Submission failed')
      }

      setSubmitted(true)
      toast.success('Thank you for contacting us. We will get back to you as soon as possible.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } catch (error) {
      toast.error(error.message || 'Oops, there was an error sending your message. Please try again later.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className='contact-us-quote-form'>
      <div className='contact-us-quote-form__inner'>
        {submitted ? (
          <div className='contact-us-quote-form__success'>
            <p>Thank you for contacting us. We will get back to you as soon as possible.</p>
          </div>
        ) : (
          <form className='contact-us-quote-form__form' onSubmit={handleSubmit} noValidate>
            <div className='contact-us-quote-form__row contact-us-quote-form__row--half'>
              <div className='contact-us-quote-form__field'>
                <label className='contact-us-quote-form__label'>
                  First Name: <span className='contact-us-quote-form__required'>*</span>
                </label>
                <input
                  type='text'
                  name='firstName'
                  className='contact-us-quote-form__input'
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className='contact-us-quote-form__field'>
                <label className='contact-us-quote-form__label'>
                  Last Name: <span className='contact-us-quote-form__required'>*</span>
                </label>
                <input
                  type='text'
                  name='lastName'
                  className='contact-us-quote-form__input'
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className='contact-us-quote-form__row'>
              <div className='contact-us-quote-form__field'>
                <label className='contact-us-quote-form__label'>
                  Email: <span className='contact-us-quote-form__required'>*</span>
                </label>
                <input
                  type='email'
                  name='email'
                  className='contact-us-quote-form__input'
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className='contact-us-quote-form__row'>
              <div className='contact-us-quote-form__field'>
                <label className='contact-us-quote-form__label'>
                  Phone: <span className='contact-us-quote-form__required'>*</span>
                </label>
                <input
                  type='tel'
                  name='phone'
                  className='contact-us-quote-form__input'
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className='contact-us-quote-form__row'>
              <div className='contact-us-quote-form__field'>
                <label className='contact-us-quote-form__label'>
                  Product: <span className='contact-us-quote-form__required'>*</span>
                </label>
                <select
                  name='product'
                  className='contact-us-quote-form__select'
                  value={formData.product}
                  onChange={handleChange}
                  required
                >
                  <option value=''></option>
                  <option value='Health Insurance'>Health Insurance</option>
                  <option value='Medicare Insurance'>Medicare Insurance</option>
                  <option value='Life Insurance'>Life Insurance</option>
                  <option value='Small Business Insurance'>Small Business Insurance</option>
                </select>
              </div>
            </div>

            <div className='contact-us-quote-form__row'>
              <div className='contact-us-quote-form__field'>
                <label className='contact-us-quote-form__label'>
                  Zip Code <span className='contact-us-quote-form__required'>*</span>
                </label>
                <input
                  type='text'
                  name='zipCode'
                  className='contact-us-quote-form__input'
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <p className='contact-us-quote-form__disclosure'>
              <em>
                {DISCLAIMER_SHORT} By submitting my contact information, I verify that the contact information entered
                is correct and is my personal information and that I am over 18 years of age. I am providing my express
                written consent agreement to permit USA Benefits Group to contact me at the number provided for
                marketing purposes, including through the use of automated technology, SMS/MMS messages, AI generated
                voice, and pre-recorded and/or artificial voice messages. I acknowledge my consent is not required to
                obtain any good or service and to be connected with sellers that fit my needs without providing consent
                I can call 1 (888) 826-1966.
              </em>
            </p>

            <div className='contact-us-quote-form__submit-row'>
              <button type='submit' className='contact-us-quote-form__submit' disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  )
}

export default ContactUsQuoteForm
